<template>
  <div class="power-ikwi-chart">
    <div class="chart-title">{{ title }}</div>
    <div class="chart-container" ref="chart"></div>
  </div>
</template>

<script>
export default {
  name: 'ChartLine',
  props: {
    title: {
      type: String,
      default: '图表'
    },
    chartType: {
      type: String,
      default: ""
    },
    chartData: {
      type: Array,
      required: true,
      default: () => []
    },
    xAxisName: {
      type: String,
      default: '转速 (rpm)'
    },
    yAxisName: {
      type: String,
      default: '功率 (kW)'
    },
    xAxisMax: {
      type: Number,
      default: 6000
    },
  },
  data() {
    return {
      myChart: null,
      calculatedYMax: 60
    };
  },
  mounted() {
    this.initChart();
  },
  watch: {
    chartData: {
      handler() {
        this.$nextTick(() => {
          this.initChart();
        });
      },
      deep: true
    }
  },
  methods: {
    calculateYMax() {
      if (!this.chartData || this.chartData.length === 0) {
        return 60;
      }

      let maxValue = 0;
      this.chartData.forEach(series => {
        if (series.valueList && series.valueList.length > 0) {
          series.valueList.forEach(point => {
            const value = parseFloat(this.chartType === "gl" ?  point.gl : point.nj) || 0;
            if (value > maxValue) {
              maxValue = value;
            }
          });
        }
      });

      return Math.ceil(maxValue * 1.1);
    },

    initChart() {
      const echarts = require('echarts');
      const chartDom = this.$refs.chart;

      if (this.myChart) {
        this.myChart.dispose();
      }

      this.myChart = echarts.init(chartDom);

      this.calculatedYMax = this.calculateYMax();

      const generateDenseData = (baseData) => {
        if (!baseData || baseData.length === 0) return [];

        const sortedData = baseData
            .filter(item => item && typeof item.zs !== 'undefined')
            .slice()
            .sort((a, b) => (Number(a.zs) || 0) - (Number(b.zs) || 0));

        if (sortedData.length === 0) return [];

        const denseData = [];
        for (let i = 0; i < sortedData.length - 1; i++) {
          const start = sortedData[i];
          const end = sortedData[i + 1];
          denseData.push([start.zs, parseFloat(this.chartType === "gl" ?  start.gl : start.nj)]);

          const steps = 20;
          for (let j = 1; j < steps; j++) {
            const x = start.zs + (end.zs - start.zs) * j / steps;
            const y = parseFloat(this.chartType === "gl" ?  start.gl : start.nj) + (parseFloat(this.chartType === "gl" ?  end.gl : end.nj) - parseFloat(this.chartType === "gl" ?  start.gl : start.nj)) * j / steps;
            denseData.push([x, y]);
          }
        }
        if (sortedData.length > 0) {
          const lastPoint = sortedData[sortedData.length - 1];
          denseData.push([lastPoint.zs,  parseFloat(this.chartType === "gl" ?  lastPoint.gl : lastPoint.nj)]);
        }
        return denseData;
      };

      const legendData = this.chartData.map(item => item.name);
      const $t = this.$t.bind(this);

      const seriesData = this.chartData.map(item => {
        const isDashed = item.name.includes('S6');
        const lineStyle = {
          width: 2,
          color: this.getLineColor(item.index),
          type: isDashed ? 'dashed' : 'solid'
        };

        return {
          name: item.name,
          type: 'line',
          data: generateDenseData(item.valueList),
          lineStyle: lineStyle,
          itemStyle: {
            color: lineStyle.color
          },
          symbol: 'none',
          smooth: true
        };
      });

      // 计算合适的Y轴间隔
      const yInterval = this.calculateYInterval(this.calculatedYMax);

      // 改造后的 tooltip 格式化函数
      const tooltipFormatter = (params) => {
        const currentX = params[0].axisValue;
        let result = `<div style="font-weight: bold; margin-bottom: 5px;">${$t('转速')}: ${currentX.toLocaleString()} rpm</div>`;

        this.chartData.forEach(series => {
          const closestPoint = series.valueList.reduce((prev, curr) => {
            if (!prev) return curr;
            const prevDiff = Math.abs(Number(prev.zs) - currentX);
            const currDiff = Math.abs(Number(curr.zs) - currentX);
            return prevDiff < currDiff ? prev : curr;
          }, null);

          const color = this.getLineColor(series.index);
          const value = closestPoint ? parseFloat(this.chartType === "gl" ? closestPoint.gl : closestPoint.nj).toFixed(1) : '--';

          // 根据 chartType 动态切换单位
          const unit = this.chartType === 'nj' ? 'N.m' : 'kW';

          result += `
      <div style="display: flex; align-items: center; margin: 5px 0;">
        <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 5px;"></span>
        <span>${series.name}: ${value} ${unit}</span>
      </div>
    `;
        });

        return result;
      };

      const option = {
        grid: {
          left: '70px',
          right: '30px',
          top: '60px',
          bottom: '50px'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            label: {
              backgroundColor: '#6a7985',
              formatter: function(params) {
                return `${$t('转速')}: ${params.value.toLocaleString()} rpm`;
              }
            }
          },
          formatter: tooltipFormatter // 使用新的格式化函数
        },
        legend: {
          data: legendData,
          top: 10,
          textStyle: {
            fontSize: 12,
            color: '#fff'
          },
          itemGap: 15
        },
        xAxis: {
          type: 'value',
          name: this.xAxisName,
          nameLocation: 'middle',
          nameGap: 25,
          min: 0,
          max: this.xAxisMax,
          interval: 2000,
          axisLine: {
            lineStyle: {
              color: '#FFFFFF'
            }
          },
          axisLabel: {
            margin: 8,
            formatter: function(value) {
              return value.toLocaleString();
            }
          }
        },
        yAxis: {
          type: 'value',
          name: this.yAxisName,
          nameLocation: 'middle',
          nameGap: 40,
          min: 0,
          max: this.calculatedYMax,
          interval: yInterval,
          axisLine: {
            lineStyle: {
              color: '#FFFFFF'
            }
          },
          axisLabel: {
            margin: 8
          }
        },
        series: seriesData
      };

      this.myChart.setOption(option);

      window.addEventListener('resize', () => {
        this.myChart.resize();
      });
    },

    calculateYInterval(maxValue) {
      if (maxValue <= 10) return 2;
      if (maxValue <= 20) return 5;
      if (maxValue <= 50) return 10;
      if (maxValue <= 100) return 20;
      return Math.ceil(maxValue / 6);
    },

    getLineColor(index) {
      if(index == 0){
        return  '#fac858';
      }else if(index == 1){
        return  '#91cc75';
      }else if(index == 2){
        return  '#c65454';
      }else{
        return  '#c65454';
      }
    }
  },
  beforeDestroy() {
    if (this.myChart) {
      this.myChart.dispose();
    }
  }
}
</script>

<style scoped>
.power-ikwi-chart {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  /* background-color: black; */
}

.chart-title {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
}

.chart-container {
  width: 100%;
  height: 450px;
}
</style>